<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { useCalendarVersionStore } from '@/stores/calendar-version';
import { useMobile } from '@/composables/useMobile';
import { usePermissions } from '@/composables/usePermissions';
import { useUser } from '@/composables/useUser';
import { shiftChangeRequestsApi } from '@/api/shift-change-requests';
import ShiftsList from './shifts-list.vue';
import ShiftChangeRequestsDialog from './shift-change-requests-dialog.vue';

// Async so the vue-cal chunk (and its CSS) only loads for users on the new version.
const CalendarV2Page = defineAsyncComponent(() => import('../calendar-v2/calendar-v2-page.vue'));

interface Props {
  patientId?: number,
  fromForm?: boolean,
}

const props = defineProps<Props>();

const { t } = useI18n();
const { useNewCalendar } = storeToRefs(useCalendarVersionStore());
const { isMobile } = useMobile();
const { hasPermission } = usePermissions();
const { user } = useUser();

const canImportShifts = computed(() => hasPermission.value('shifts', 'bulk_import') &&
  !!user?.value?.companyShiftImportsEnabled);

// The persisted store keeps the boolean flag; the toggle exposes it as two
// named views: the classic day-columns table vs the v2 hourly agenda.
const selectedView = computed({
  get: () => (useNewCalendar.value ? 'agenda' : 'calendar'),
  set: (value: 'calendar' | 'agenda') => {
    useNewCalendar.value = value === 'agenda';
  },
});

const showChangeRequests = computed(() => hasPermission.value('shifts', 'index') &&
  !!user?.value?.companyShiftChangeRequestsEnabled);
const changeRequestsOpen = ref(false);

// Same key prefix as the dialog's query, so approving/rejecting there refreshes this
// badge; the dialog's own filters live under a longer key and never narrow this count.
const { data: pendingChangeRequests } = useQuery({
  queryKey: ['shiftChangeRequests', 'pending'],
  queryFn: () => shiftChangeRequestsApi.index({ aasmState: 'pending' }),
  enabled: showChangeRequests,
});
const pendingChangeRequestCount = computed(() => pendingChangeRequests.value?.length ?? 0);

// The filter drawer is shared with whichever calendar is active. Owning the
// open-state here lets the mobile filter button sit on the version-toggle row,
// clear of the calendar card title. Open by default on desktop, closed on mobile.
const filtersDrawer = ref(!isMobile.value);
const activeFilterCount = ref(0);

// The toggle stays hidden (visibility, so the row never jumps) until the
// active view reports its content is on screen — floating above a skeleton or
// the async v2 chunk gap reads as a glitch. Reset on swap: the incoming view
// re-reports once it is up.
const contentReady = ref(false);
watch(useNewCalendar, () => {
  contentReady.value = false;
});

// ShiftsList declares its props as required, so undefined values are omitted
// instead of forwarded (avoids the missing-required-prop dev warning). The
// assertion matches ShiftsList's declared props; at runtime it has always
// tolerated absent values (the Rails prop extraction can deliver none).
const forwardedProps = computed(() => ({
  ...(props.patientId !== undefined && { patientId: props.patientId }),
  ...(props.fromForm !== undefined && { fromForm: props.fromForm }),
} as { patientId: number, fromForm: boolean }));
</script>
<template>
  <div class="w-100">
    <div class="d-flex align-center px-4 mt-n4 mb-2">
      <v-badge
        v-if="isMobile"
        :model-value="activeFilterCount > 0"
        :content="activeFilterCount"
        color="primary"
        offset-x="10"
        offset-y="10"
        class="mr-2"
      >
        <v-btn
          icon="mdi-filter"
          variant="text"
          aria-label="Abrir filtros"
          @click="filtersDrawer = true"
        />
      </v-badge>
      <v-btn-toggle
        v-model="selectedView"
        color="primary"
        density="compact"
        variant="outlined"
        divided
        mandatory
        :class="{ 'view-toggle--hidden': !contentReady }"
      >
        <v-btn
          value="calendar"
          size="small"
          :aria-label="t('calendarV2.viewToggle.calendar')"
        >
          <v-icon icon="mdi-calendar-month" />
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            {{ t('calendarV2.viewToggle.calendar') }}
          </v-tooltip>
        </v-btn>
        <v-btn
          value="agenda"
          size="small"
          :aria-label="t('calendarV2.viewToggle.agenda')"
        >
          <v-icon icon="mdi-view-agenda-outline" />
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            {{ t('calendarV2.viewToggle.agenda') }}
          </v-tooltip>
        </v-btn>
      </v-btn-toggle>
      <v-spacer />
      <v-badge
        v-if="showChangeRequests"
        :model-value="pendingChangeRequestCount > 0"
        :content="pendingChangeRequestCount"
        color="error"
        offset-x="6"
        offset-y="6"
        class="mr-2"
      >
        <v-btn
          icon
          color="primary"
          variant="outlined"
          density="comfortable"
          aria-label="Solicitudes de cambio de turno"
          @click="changeRequestsOpen = true"
        >
          <v-icon icon="mdi-swap-horizontal" />
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            Solicitudes de cambio de turno
          </v-tooltip>
        </v-btn>
      </v-badge>
      <v-btn
        v-if="canImportShifts"
        icon
        color="primary"
        variant="outlined"
        density="comfortable"
        href="/shift_imports/new"
        :aria-label="t('shiftImport.entryPoint')"
      >
        <v-icon icon="mdi-file-upload" />
        <v-tooltip
          activator="parent"
          location="bottom"
        >
          {{ t('shiftImport.entryPoint') }}
        </v-tooltip>
      </v-btn>
    </div>
    <shift-change-requests-dialog
      v-if="showChangeRequests"
      v-model="changeRequestsOpen"
    />
    <calendar-v2-page
      v-if="useNewCalendar"
      v-model:drawer="filtersDrawer"
      v-model:active-filter-count="activeFilterCount"
      v-model:content-ready="contentReady"
      v-bind="forwardedProps"
    />
    <shifts-list
      v-else
      v-model:drawer="filtersDrawer"
      v-model:active-filter-count="activeFilterCount"
      v-model:content-ready="contentReady"
      v-bind="forwardedProps"
    />
  </div>
</template>
<style scoped>
.view-toggle--hidden {
  visibility: hidden;
}
</style>
